import React, { useState, useEffect } from 'react';
import { Icon, mergeStyleSets, Stack, Text } from "@fluentui/react";
import { Menu, MenuItem, MenuList, MenuPopover, MenuTrigger } from "@fluentui/react-components";
import { useLearnByRole } from "../../hooks/useHooks";
import { Spinner, SpinnerSize } from "@fluentui/react/lib/Spinner";
import { commonStyles } from "../CommonStyles/CommonStyles";
import LearningCard from "../Cards/LearningCard";
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import './LearnByRole.scss';

const styles = mergeStyleSets({
    link: {
        color: "#0074D3", textDecoration: "none", fontSize: "16px"
    },
    rightArrow: { position: "relative", top: "3px", marginRight: "10px" },
    showMoreButton: {
        backgroundColor: 'transparent',
        color: '#0078D7',
        border: 'none',
        padding: '10px 0px',
        cursor: 'pointer',
        fontSize: 14,
        borderRadius: 4,
        fontWeight: 600,
        display: 'flex',
        lineHeight: 18.62,
        selectors: {
            ':hover': {
                textDecoration: 'underline'
            },
        },
    },
    arrowIcon: {
        position: "relative",
        marginLeft: 8,
        fontWeight: 600,
        fontSize: 14,
        color: '#0078D7',
        selectors: {
            ':hover': {
                textDecoration: 'none !important'
            },
        },
    },

    tabContainer: {
        display: 'flex',
        marginBottom: '20px',
    },
    tab: {
        padding: '10px 20px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: 600,
        color: '#333',
    },
    activeTab: {
        borderBottom: '4px solid #0078D7',
        color: '#0078D7',
    },
    menuButton: {
        // padding: '10px',
        fontSize: '14px',
        fontWeight: 600,
        cursor: 'pointer',
        backgroundColor: 'transparent',
        border: '1px solid #0078D7',
        padding: '5px 15px',
        height: '35px',
        borderWidth: '0px 0px 4px',
        color: '#0078D7',
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
        textAlign: 'left',
        marginBottom: '20px',
    },
    menuPopover: {
        width: '100%',
        left: 0,
        right: 0,
        top: '50px',
        position: 'fixed',
        border: '1px solid #0078D7',
    },
    menuList: {
        //width: '100%',
        width: '100%',
        maxWidth: 'none',
        color: 'rgb(0, 66, 117) !important',
        fontSize: '14px',
        fontWeight: '600',
    },
    menuItem: {
        width: '100% !important',
        maxWidth: 'none !important',
        padding: '10px',
        color: 'rgb(0, 66, 117) !important',
        fontSize: '14px',
        fontWeight: '600',
    }
});

export const LearnByRole: React.FC = () => {
    const { data, isLoading, error } = useLearnByRole();
    const [visibleCards, setVisibleCards] = React.useState(4);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);
    const [showMore, setShowMore] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const queryParams = new URLSearchParams(window.location.search);

    const Industry: string[] = Array.from(
        new Set(data?.map(item => item.industry).filter(industry => industry && industry.trim() !== "" && industry !== "null"))
    ) as string[];

    const industryAll = t('LearnByTopic.IndustryAll', 'null')?.trim();
    const industryParam = queryParams.get('industry')?.toLowerCase();
    const uniqueIndustry: string[] = Industry.length > 0 ? [industryAll, ...Industry] : [];

    const [activeTab, setActiveTab] = useState<string>(industryAll);


    const handleResize = () => {
        setIsMobile(window.innerWidth <= 600);
    };

    const toggleShowMore = () => {
        setShowMore(!showMore);
        setVisibleCards(showMore ? 4 : data.length);
    };

    useEffect(() => {
        if (data) {
            const defaultTab = uniqueIndustry.find(item => item.toLowerCase() === industryParam) ?? industryAll;
            if (defaultTab !== activeTab)
                setActiveTab(defaultTab);
        }
    }, [data]);

    useEffect(() => {
        if (error) {
            navigate('/error');
        }
    }, [error, navigate]);

    useEffect(() => {
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    if (!data?.length) {
        return null;
    }

    let filteredData = data;
    if (uniqueIndustry.length > 0)
        filteredData = activeTab === industryAll ? data.filter(item => !item.industry || item.industry === "" ||  item.industry === "null") : data.filter(item => item.industry === activeTab);

    return (
        <Stack className='spotlightContainer'>
            <Stack className={commonStyles.container}>
                {isLoading && <Spinner size={SpinnerSize.large} />}
                {data?.length && !isLoading && (
                    <>
                        <Stack style={{ marginTop: "20px" }}>
                            <Text className={commonStyles.heading}>{t('LearnByRole.topicHeading')}</Text>
                            <Text  as="h2"  className={commonStyles.subHeading}>{t('LearnByRole.topicSubHeading')}</Text>
                        </Stack>
                        {uniqueIndustry.length > 1 && (
                            isMobile ? (
                                <Menu>
                                    <MenuTrigger disableButtonEnhancement>
                                        <button className={styles.menuButton} onClick={() => setMenuOpen(!menuOpen)}>
                                            <span>{activeTab}</span> <Icon iconName={menuOpen ? 'ChevronUp' : 'ChevronDown'} />
                                        </button>
                                    </MenuTrigger>
                                    <div style={{ width: '100vw' }}>
                                        <MenuPopover className={styles.menuPopover}>
                                            <MenuList className={styles.menuList}>
                                                {uniqueIndustry.map(tab => (
                                                    <MenuItem className={styles.menuItem} key={tab} onClick={() => { setActiveTab(tab); setMenuOpen(false); }}>
                                                        {tab}
                                                    </MenuItem>
                                                ))}
                                            </MenuList>
                                        </MenuPopover>
                                    </div>
                                </Menu>
                            ) : (
                                <Stack horizontal className={styles.tabContainer}>
                                    {uniqueIndustry.map((tab) => (
                                        <Text
                                            key={tab}
                                            className={`${styles.tab} ${activeTab === tab ? styles.activeTab : ''}`}
                                            onClick={() => setActiveTab(tab)}
                                        >
                                            {tab}
                                        </Text>
                                    ))}
                                </Stack>
                            )
                        )}
                        <Stack horizontal className={commonStyles.cardsContainer}>
                            {filteredData.slice(0, isMobile ? visibleCards : filteredData.length).map((x, index) => (
                                <Stack key={index}>
                                    <LearningCard cardData={x} />
                                </Stack>
                            ))}
                        </Stack>
                        {isMobile && (
                            <button onClick={toggleShowMore} className={styles.showMoreButton}>
                                {showMore ? t('ShowLess') : t('ShowMore')}
                                <Icon iconName={showMore ? 'ChevronUp' : 'ChevronDown'} className={styles.arrowIcon} />
                            </button>
                        )}
                    </>
                )}
            </Stack>
        </Stack>
    );
};
